import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import {
  BehaviorSubject,
  Subject,
  debounceTime,
  fromEvent,
  takeUntil,
} from "rxjs";
import { ADS_MANAGEMENT_ROUTE, GOLD_ADS_MANAGEMENT_ROUTE, ALERT_ROUTE, BANK_MANAGEMENT_ROUTE, WORKER, COMPANY, CONTRACT_MANAGEMENT_ROUTE, CONTROVERSY_CENTER_ROUTE, CUSTOMER_COMPANY_ROUTE, EMPLOYEE_ROUTE, INVITE_COLLABORATORS_ROUTE, USER_MANAGEMENT_ROUTE, WEB_MANAGEMENT_ROUTE, WHISTLEBLOWING_ROUTE } from "../constants/constants";
import { AuthService } from "./auth.service";

export interface Menu {
  headTitle1?: string;
  level?: number;
  headTitle2?: string;
  path?: string;
  title?: string;
  type?: string;
  icon?: string;
  iconPath?: string;
  active?: boolean;
  bookmark?: boolean;
  pinnedData?: boolean;
  items?: Menu[];
  children?: Menu[];
  allowedRoles?: string[]; // Roles that can access this menu item
}

@Injectable({
  providedIn: "root",
})
export class NavService {
  public screenWidth: BehaviorSubject<number> = new BehaviorSubject(
    window.innerWidth
  );

  private unsubscriber: Subject<any> = new Subject();

  public language: boolean = false;

  public collapseSidebar: boolean = window.innerWidth < 1200 ? true : false;

  constructor(
    private router: Router,
    private authService: AuthService
  ) {
    this.setScreenWidth(window.innerWidth);
    fromEvent(window, "resize")
      .pipe(debounceTime(0), takeUntil(this.unsubscriber))
      .subscribe((evt: any) => {
        this.setScreenWidth(evt.target.innerWidth);
        if (evt.target.innerWidth < 1200) {
          this.collapseSidebar = true;
        } else {
          this.collapseSidebar = false;
        }
      });
  }

  private setScreenWidth(width: number): void {
    this.screenWidth.next(width);
  }

  MENUITEMS: Menu[] = [
    {
      level: 1,
      title: "company_management",
      icon: "",
      iconPath: "assets/svg/sidebar-customer-company.svg",
      type: "link",
      active: false,
      path: CUSTOMER_COMPANY_ROUTE,
    },
    {
      level: 1,
      title: "employee_management",
      icon: "",
      iconPath: "assets/svg/sidebar-employee.svg",
      type: "link",
      active: false,
      path: EMPLOYEE_ROUTE,
    },
    {
      level: 1,
      title: "ads_management",
      icon: "",
      iconPath: "assets/svg/sidebar-ad.svg",
      type: "link",
      active: false,
      path: ADS_MANAGEMENT_ROUTE,
    },
    {
      level: 1,
      title: "gold_ads_management",
      icon: "",
      iconPath: "assets/svg/sidebar-ad.svg",
      type: "link",
      active: false,
      path: GOLD_ADS_MANAGEMENT_ROUTE,
    },
    {
      level: 1,
      title: "contract_management",
      icon: "",
      iconPath: "assets/svg/sidebar-contract.svg",
      active: false,
      type: 'sub',
      children: [
        {
          path: `${CONTRACT_MANAGEMENT_ROUTE}/${COMPANY}`,
          title: 'contract_management_company',
          type: 'link',
          active: false,
        },
        {
          path: `${CONTRACT_MANAGEMENT_ROUTE}/${WORKER}`,
          title: 'contract_management_worker',
          type: 'link',
          active: false,
        },
      ]
    },
    {
      level: 1,
      title: "bank_management",
      icon: "",
      iconPath: "assets/svg/sidebar-bank.svg",
      active: false,
      type: 'sub',
      children: [
        {
          path: `${BANK_MANAGEMENT_ROUTE}/${COMPANY}`,
          title: 'bank_management_company',
          type: 'link',
          active: false,
        },
        {
          path: `${BANK_MANAGEMENT_ROUTE}/${WORKER}`,
          title: 'bank_management_worker',
          type: 'link',
          active: false,
        },
      ]
    },
    {
      level: 1,
      title: "controversy_center",
      icon: "",
      iconPath: "assets/svg/sidebar-whistleblowing.svg",
      type: "link",
      active: false,
      path: CONTROVERSY_CENTER_ROUTE,
    },
    {
      level: 1,
      title: "whistleblowing",
      icon: "",
      iconPath: "assets/svg/sidebar-whistleblowing.svg",
      type: "link",
      active: false,
      path: WHISTLEBLOWING_ROUTE,
    },
    {
      level: 1,
      title: "web_management",
      icon: "",
      iconPath: "assets/svg/sidebar-web.svg",
      type: "link",
      active: false,
      path: WEB_MANAGEMENT_ROUTE,
    },
    {
      level: 1,
      title: "alerts",
      icon: "",
      iconPath: "assets/svg/sidebar-alert.svg",
      type: "link",
      active: false,
      path: ALERT_ROUTE,
    },
    {
      level: 1,
      title: "collaborators_invitation",
      icon: "",
      iconPath: "assets/svg/sidebar-customer-company.svg",
      type: "link",
      active: false,
      path: INVITE_COLLABORATORS_ROUTE,
      allowedRoles: ['admin'], // Only admin can see this menu item
    },
    {
      level: 1,
      title: "user_management",
      icon: "",
      iconPath: "assets/svg/sidebar-employee.svg",
      type: "link",
      active: false,
      path: USER_MANAGEMENT_ROUTE,
      allowedRoles: ['admin'], // Only admin can see this menu item
    },
  ];
  items = new BehaviorSubject<Menu[]>(this.MENUITEMS);

  /**
   * Filter menu items based on user role
   * If a menu item has allowedRoles, only show it to users with those roles
   * If no allowedRoles specified, show to all authenticated users
   */
  private filterMenuItemsByRole(): void {
    const userRole = this.authService.getRoleName();
    
    if (!userRole) {
      this.items.next([]);
      return;
    }

    const filteredMenuItems = this.MENUITEMS.filter((menuItem) => {
      // If no allowedRoles specified, show to all authenticated users
      if (!menuItem.allowedRoles || menuItem.allowedRoles.length === 0) {
        return true;
      }
      
      // Check if user role is in the allowed roles
      return menuItem.allowedRoles.some(
        (role) => role.toLowerCase() === userRole.toLowerCase()
      );
    });

    this.items.next(filteredMenuItems);
  }

  /**
   * Refresh menu items when user role changes
   */
  refreshMenuItems(): void {
    this.filterMenuItemsByRole();
  }

  ngOnDestroy() {
    this.unsubscriber.complete();
  }
}
